import { useEffect, useState } from 'react';
import { toast, Toaster } from 'sonner';
import { useOfficeReady } from '@/hooks/useOfficeReady';
import { useTheme } from '@/hooks/useTheme';
import { useAuthStore } from '@/stores/authStore';
import { LoginScreen } from '@/components/auth/LoginScreen';
import { ForgotPasswordScreen } from '@/components/auth/ForgotPasswordScreen';
import { ResetPasswordScreen } from '@/components/auth/ResetPasswordScreen';
import { MainLayout } from '@/components/layout/MainLayout';
import { LoadingSplash } from '@/components/common/LoadingSplash';
import { hydratePreferencesIntoChatStore } from '@/services/preference_sync';
import { storage } from '@/services/storage';

// AuthRoute is the subset of URL hashes we react to on the unauthenticated
// side. Everything after the first "?" is treated as query string and
// parsed by the individual screens (e.g. ResetPasswordScreen reads the
// token from the hash's query portion).
type AuthRoute = 'login' | 'forgot' | 'reset';

function parseAuthRoute(hash: string): AuthRoute {
  const stripped = (hash || '').replace(/^#/, '').split('?')[0];
  if (stripped === 'forgot-password') return 'forgot';
  if (stripped === 'reset-password') return 'reset';
  return 'login';
}

export function App() {
  const ready = useOfficeReady();
  useTheme(); // Tailwind .dark toggle driven by prefs.theme / OS
  const { isAuthenticated, hydrate } = useAuthStore();
  const [authRoute, setAuthRoute] = useState<AuthRoute>(() => parseAuthRoute(window.location.hash));

  useEffect(() => {
    // Verify stored token is still live on app boot.
    hydrate();
  }, [hydrate]);

  // Global 401 / expired-token handler. The axios response interceptor
  // in services/api.ts dispatches an `auth:expired` event whenever a
  // refresh attempt fails (refresh token also expired, key revoked,
  // etc.). Without this listener the taskpane stayed visually "logged
  // in" while every network call silently failed — the user saw
  // repeated "chat request failed" errors with no indication that
  // re-login was required. We clear local state, show a toast, and
  // flip back to the login screen so recovery is obvious.
  useEffect(() => {
    const onExpired = () => {
      storage.clear();
      // authStore's state reset happens via hydrate() next boot, but
      // we also need the in-memory flip immediately:
      useAuthStore.setState({ user: null, isAuthenticated: false });
      toast.error('Session expired. Please log in again.');
    };
    window.addEventListener('auth:expired', onExpired);
    return () => window.removeEventListener('auth:expired', onExpired);
  }, []);

  useEffect(() => {
    // Once authenticated, pull server-side preferences and apply the
    // user's default provider/model into chatStore (subject to catalog
    // validation). localStorage may have already populated a recent
    // selection — server wins on conflict for cross-device consistency.
    if (isAuthenticated) {
      void hydratePreferencesIntoChatStore();
    }
  }, [isAuthenticated]);

  // Track hash changes so links like "#forgot-password" swap the screen
  // without a reload. The reset link we email lands here too.
  useEffect(() => {
    const onHash = () => setAuthRoute(parseAuthRoute(window.location.hash));
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  if (!ready) return <LoadingSplash message="Starting Excel Autopilot…" />;

  // When the user is already signed in and they land on a reset link,
  // showing the reset form is still the right thing — they may be
  // resetting their own password. Login/forgot screens are hidden behind
  // the auth guard otherwise.
  const screen = (() => {
    if (authRoute === 'reset') return <ResetPasswordScreen />;
    if (isAuthenticated) return <MainLayout />;
    if (authRoute === 'forgot') return <ForgotPasswordScreen />;
    return <LoginScreen />;
  })();

  return (
    <div className="flex h-full flex-col bg-white dark:bg-[#1A1A1A]">
      {screen}
      <Toaster position="top-center" richColors closeButton />
    </div>
  );
}
